import React from "react";
import moment from "moment";

export const QUARTER_TABS = [
  { id: "all", label: "All" },
  { id: "Q1", label: "Q1" },
  { id: "Q2", label: "Q2" },
  { id: "Q3", label: "Q3" },
  { id: "Q4", label: "Q4" },
];

export function formatPeriodDate(value) {
  if (!value) return "—";
  return String(value).slice(0, 10);
}

export function getPeriodDays(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const start = moment(startDate);
  const end = moment(endDate);
  if (!start.isValid() || !end.isValid()) return null;
  return end.diff(start, "days") + 1;
}

/** Quarters covered by a date range (e.g. Oct–Dec → Q4 2025). */
export function getQuartersForPeriod(startDate, endDate) {
  if (!startDate || !endDate) return [];

  const start = moment(startDate).startOf("month");
  const end = moment(endDate).startOf("month");
  if (!start.isValid() || !end.isValid()) return [];

  const seen = new Set();
  const quarters = [];
  const cursor = start.clone();

  while (cursor.isSameOrBefore(end)) {
    const quarter = cursor.quarter();
    const year = cursor.year();
    const key = `${year}-Q${quarter}`;
    if (!seen.has(key)) {
      seen.add(key);
      quarters.push({ quarter, year, label: `Q${quarter} ${year}` });
    }
    cursor.add(1, "month");
  }

  return quarters;
}

export function periodMatchesQuarterFilter(startDate, endDate, quarterFilter) {
  if (!quarterFilter || quarterFilter === "all") return true;
  const qNum = Number(String(quarterFilter).replace("Q", ""));
  if (!qNum) return true;
  return getQuartersForPeriod(startDate, endDate).some((item) => item.quarter === qNum);
}

/** Calendar start/end for a fiscal quarter. */
export function getQuarterDateRange(quarter, year) {
  const q = Number(quarter);
  const y = Number(year);
  if (!q || q < 1 || q > 4 || !y) return null;

  const start = moment().year(y).quarter(q).startOf("quarter");
  const end = moment().year(y).quarter(q).endOf("quarter");

  return {
    start: start.toDate(),
    end: end.toDate(),
    startFormatted: start.format("YYYY-MM-DD"),
    endFormatted: end.format("YYYY-MM-DD"),
    label: `Q${q} ${y}`,
  };
}

const CURRENCY_TONE = {
  EUR: "eur",
  USD: "usd",
  GBP: "gbp",
  TND: "tnd",
};

export function ExchangeRateChip({ currency, value }) {
  const tone = CURRENCY_TONE[currency] || "default";
  return (
    <span className={`ex-rate-chip ex-rate-chip--${tone}`}>
      <span className="ex-rate-chip__code">{currency}</span>
      <span className="ex-rate-chip__value">{String(value)}</span>
    </span>
  );
}

export function ExchangeQuarterBadge({ label }) {
  return <span className="ex-quarter-badge">{label}</span>;
}

export function ExchangeQuarterBadges({ startDate, endDate }) {
  const quarters = getQuartersForPeriod(startDate, endDate);
  if (!quarters.length) return <span className="text-muted">—</span>;

  return (
    <div className="ex-quarters">
      {quarters.map((item) => (
        <ExchangeQuarterBadge key={`${item.year}-Q${item.quarter}`} label={item.label} />
      ))}
    </div>
  );
}

export function ExchangePeriodCell({ startDate, endDate }) {
  const days = getPeriodDays(startDate, endDate);

  return (
    <div className="ex-period">
      <span className="ex-period__range">
        {formatPeriodDate(startDate)} → {formatPeriodDate(endDate)}
      </span>
      {days ? (
        <span className="ex-period__days">
          {days} day{days === 1 ? "" : "s"}
        </span>
      ) : null}
    </div>
  );
}

export function ExchangeCurrencyCount({ count }) {
  return (
    <span className="ex-currency-count">
      {count} curr{count === 1 ? "" : "s"}
    </span>
  );
}
